import imageSize from "image-size"
import { ISizeCalculationResult } from "image-size/types/interface"
import { Pair } from "@/types/base/pair"
import { LLMID, ModelProvider } from "@/types/base"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from "@/utils/errorLogging"

const logger = createIsomorphicLogger('lib.pricingUtils')

export function hasSize(size: Pair<number, number>): boolean {
	return size instanceof Pair && size.getA() > 0 && size.getB() > 0
}

export function getImageSize(input: Uint8Array): Pair<number, number> {
	try {
		const dimensions = imageSize(input)
		const size: ISizeCalculationResult = dimensions.images ? dimensions.images[0] : dimensions
		if (size.height && size.width) {
			return new Pair(Math.floor(size.width), Math.floor(size.height))
		}
		return new Pair(0, 0)
	} catch (err) {
		logError(logger, err)
		return new Pair(-1, -1)
	}
}

export function getImageTokens(provider: ModelProvider | "azure" | "local", model: LLMID, size: Pair<number, number>): number {
	switch (provider) {
		case "openai":
		case "aidon":
		case "azure":
		case "custom":  //for us this is Rivet which will use OpenAI for images
			if (model == `gpt-4o-mini`) {
				return miniImageTokens(size.getA(), size.getB())
			}
			else {
				return visionImageTokens(size.getA(), size.getB())
			}
		case "xai":
			return xaiImageTokens(size.getA(), size.getB())
		case "openrouter":
			return visionImageTokens(size.getA(), size.getB())  //unknown but if supported for now will use OpenAI to estimate
		case "google":
			return 258  //always 258 tokens ("Gemini API considers images to be a fixed size, so they consume a fixed number of tokens (currently 258 tokens), regardless of their display or file size.")
		case "anthropic":
			return anthropicImageTokens(size.getA(), size.getB())
		case "perplexity":
			return 0  //No supported as of Sept 3rd, 2024 see:https://github.com/ppl-ai/api-discussion/discussions/20
		case "groq":
			return 0  //No model marked as supported
		case "local":
		case "ollama":
			return 0  //Those are local and don't support images
		default:
			logger.error(`Unknown provider to get image tokens: ${provider}`)
			return 0  //Well guess we don't know
	}
}

function anthropicImageTokens(width: number, height: number): number {
	return Math.floor((width * height) / 750)
}

function xaiImageTokens(width: number, height: number): number {
	const tiles = calcXaiImageTiles(width, height)
	const tileCnt = (tiles.getA() * tiles.getB()) + 1
	return Math.min(tileCnt, 6)
}

function visionImageTokens(width: number, height: number, detail: string = 'high'): number {
	const tiles = calcOpenAiImageTiles(width, height, detail)
	return Math.floor(85 + 170 * (tiles.getA() * tiles.getB()))
}

function miniImageTokens(width: number, height: number, detail: string = 'high'): number {
	const tiles = calcOpenAiImageTiles(width, height, detail)
	return Math.floor(2833 + 5667 * (tiles.getA() * tiles.getB()))
}

export function calcOpenAiImageTiles(width: number, height: number, detail: string = 'high'): Pair<number, number> {
	if (detail === 'low') {
		return new Pair(0, 0)
	}

	// Scale down to fit within a 2048 x 2048 square if necessary
	if (width > 2048 || height > 2048) {
		const maxSize = 2048
		const aspectRatio = width / height
		if (aspectRatio > 1) {
			width = maxSize
			height = parseInt(String(maxSize / aspectRatio))
		} else {
			height = maxSize
			width = parseInt(String(maxSize * aspectRatio))
		}
	}

	// Resize such that the shortest side is 768px if the original dimensions exceed 768px
	const minSize = 768
	const aspectRatio = width / height
	if (width > minSize && height > minSize) {
		if (aspectRatio > 1) {
			height = minSize
			width = parseInt(String(minSize * aspectRatio))
		} else {
			width = minSize
			height = parseInt(String(minSize / aspectRatio))
		}
	}

	const tilesWidth = Math.ceil(width / 512)
	const tilesHeight = Math.ceil(height / 512)
	return new Pair(tilesWidth, tilesHeight)
}

export function calcXaiImageTiles(width: number, height: number): Pair<number, number> {
	const tilesWidth = Math.ceil(width / 448)
	const tilesHeight = Math.ceil(height / 448)
	return new Pair(tilesWidth, tilesHeight)
}

function _runTests() {
	const testCases = [
		{ width: 128, height: 128, detail: 'high', expected: 255 },
		{ width: 512, height: 512, detail: 'high', expected: 255 },
		{ width: 612, height: 134, detail: 'high', expected: 425 },
		{ width: 767, height: 767, detail: 'high', expected: 765 },
		{ width: 900, height: 767, detail: 'high', expected: 765 },
		{ width: 900, height: 900, detail: 'high', expected: 765 },
		{ width: 3000, height: 1200, detail: 'high', expected: 1445 },
		{ width: 3000, height: 5000, detail: 'high', expected: 1105 },
		{ width: 4096, height: 8192, detail: 'low', expected: 85 },
	];

	let allTestsPassed = true;

	for (const test of testCases) {
		const { width, height, detail, expected } = test;
		const result = visionImageTokens(width, height, detail);
		const passed = result === expected;
		allTestsPassed = allTestsPassed && passed;
		logger.info(`Test ${passed ? 'PASSED' : 'FAILED'}: width=${width}, height=${height}, detail=${detail}, expected=${expected}, got=${result}`)
	}

	if (allTestsPassed) {
		logger.info('All tests passed!');
	} else {
		logger.info('Some tests failed.');
	}
}